import React, { Component, useState, useRef, useEffect } from 'react';
import {
    IconButton,
    Tab,
    Tabs,
    InputAdornment,
    Toolbar,
    Grid,
    Card,
    Button,
    CardContent,
    Dialog,
    DialogContent,
    Typography,
    Tooltip,
    DialogActions,
    Paper,
    Grow,
    Popper,
    ClickAwayListener,
    InputLabel,
    MenuItem,
    Divider,
    RadioGroup,
    FormControlLabel,
    Radio,
} from '@mui/material';
import { Sync as RefreshIcon, DeleteForever as CloseIcon, LibraryAdd as CopyIcon, Create as EditIcon, GetApp as DownloadIcon, Add as AddIcon, AddCircle as AddCircleIcon } from '@mui/icons-material';
import { Responsive, WidthProvider } from "react-grid-layout";
import axios from 'axios';
import Config from '../../config';
import FilterOdata from '../../components/filter';
import ExtendedSlider from '../../components/slider';
import Helpers, { dollarFormat, getPartnerGroups } from '../../helpers';
import { ResponsiveLine } from '@nivo/line';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsivePie } from '@nivo/pie';
import './reports.css';
import moment from 'moment';
import FileSaverBase from 'filesaver.js-npm';
import { Link } from 'react-router-dom';
import isEqual from 'lodash.isequal';
import ExtendedField from '../../components/text_field';
import { Form as FormBase, Field as FieldBase } from 'react-final-form';
import { SketchPicker as Picker } from 'react-color';
import Select from '../../components/select_field';
import DownloadButton from '../../components/download_button';
import { FieldArray } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';
import { getOperators, loadProfiles, getDownloadLink } from '../files/export';
import Auth from '../../auth';
import ContentLoader from '../../components/content_loader';
import AutoComplete from '../../components/suggest_field';
import { DefaultDashboard, StandardSupplier, StandardRetailer } from './default_reports';
import type { DashboardProfile, WidgetFilter } from './default_reports';
import { StorageHelper } from "../../helpers";
import Moment from "react-moment";
import { SavedSearchManager } from '../../components/saved_search';
import ColorBox from "../catalogmanager/containers/catalog/containers/dashboard/components/ColorBox";

// react-final-form's Form/Field generics don't line up with this file's loose
// value shapes; cast to any at the import boundary (same pattern as
// scorecards.tsx and testing/cases.tsx).
const Form: any = FormBase;
const Field: any = FieldBase;
// filesaver.js-npm ships no type declarations (matches pages/inventory/v1/
// catalog_export.tsx and pages/orders/bulk_pick.tsx's cast).
const FileSaver: any = FileSaverBase;

// @nivo/bar and @nivo/pie ship stale/mismatched .d.ts files (e.g. colorBy is
// declared as required, and colorBy's callback param type doesn't match real
// usage) -- same issue already worked around in
// catalogmanager/.../DataBox.tsx. Cast to any at the import boundary rather
// than fighting the stale types prop by prop.
const AnyResponsiveBar: any = ResponsiveBar;
const AnyResponsivePie: any = ResponsivePie;

type DisplayItem = {
    background?: string;
    thresholdBackground?: string;
    threshold?: string | number;
    filter?: WidgetFilter | { logic?: string; filters: any[] };
    i: string;
    title: string;
    type: 'indicator' | 'chart';
    x: number;
    y: number;
    w: number;
    h: number;
    baseUnit?: string;
    yField?: string;
    chartType?: string;
    interval?: string | number;
};


const ResponsiveReactGridLayout = WidthProvider(Responsive);

const AvailableFields = [
    { FieldTitle: "Document Type", FieldName: "documentType", type: "string" },
    { FieldTitle: "Status", FieldName: "statusDescription", type: "string" },
    { FieldTitle: "Status Code", FieldName: "status", type: "number" },
    { FieldTitle: "Sender", FieldName: "companyName", type: "string" },
    { FieldTitle: "Receiver", FieldName: "partnerCompanyName", type: "string" },
    { FieldTitle: "Date", FieldName: "date", type: "date" },
    { FieldTitle: "Status Change Date", FieldName: "statusChangeDate", type: "date" },
    { FieldTitle: "System", FieldName: "source", type: "string" }
];
const AvailableChartFields = [
    { FieldTitle: "Document Type", FieldName: "DocumentType", type: "string" },
    { FieldTitle: "Sender", FieldName: "CompanyName", type: "string" },
    { FieldTitle: "Receiver", FieldName: "PartnerCompanyName", type: "string" }
];

const defaultColors = ["#01B8AA", "#F2C80F", "#F9A456", "#FD625E", "#5F6B6D", "#374649", "#6CBB19", "#078CDD"];
const colorMap = {
    "#ffe0e3": "#FD625E",
    "#eafad9": "#6CBB19",
    "#d8f0ff": "#078CDD",
    "#f5edfd": "#01B8AA",
    "#fdf4ec": "#F9A456"
};

const updateColor = (oldColor) => {
    let matchColor = (oldColor || '').toLowerCase();
    if (colorMap[matchColor]) {
        return colorMap[matchColor];
    }
    return oldColor;
}

const rgbToHsl = (r, g, b) => {
    r /= 255;
    g /= 255;
    b /= 255;

    var max = Math.max(r, g, b), min = Math.min(r, g, b);
    var h, s, l = (max + min) / 2;

    if (max === min) {
        h = s = 0; // achromatic
    } else {
        var d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
            default: break;
        }

        h /= 6;
    }

    return [h, s, l];
}

const hslToRgb = (h, s, l) => {
    let r = 0;
    let g = 0;
    let b = 0;

    if (s === 0) {
        r = g = b = l; // achromatic
    } else {
        const hue2rgb = (p: number, q: number, t: number) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };

        var q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        var p = 2 * l - q;

        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }

    return [r * 255, g * 255, b * 255];
}

const getFontColor = (hexcolor?: string, lightenOrDarken?: boolean) => {
    hexcolor = hexcolor || "#ffffff";
    hexcolor = hexcolor.indexOf('#') === 0 ? hexcolor.substring(1) : hexcolor;
    var r = parseInt(hexcolor.substr(0, 2), 16);
    var g = parseInt(hexcolor.substr(2, 2), 16);
    var b = parseInt(hexcolor.substr(4, 2), 16);
    var yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    if (lightenOrDarken) {
        let hsl = rgbToHsl(r, g, b);
        if (yiq >= 128) {
            hsl[2] = Math.max(0, hsl[2] - 0.5);
        } else {
            hsl[2] = Math.min(1, hsl[2] + 0.5);
        }
        let rgb = hslToRgb(hsl[0], hsl[1], hsl[2]);
        return `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
    }
    return (yiq >= 128) ? 'black' : 'white';
};

const camelCaseFilter = (filters) => {
    if (filters instanceof Array) {
        for (var i = 0; i < filters.length; i++) {
            camelCaseFilter(filters[i]);
        }
    }
    if (filters.field && filters.field.substring(0, 1).toLowerCase() !== filters.field.substring(0, 1)) {
        filters.field = filters.field.substring(0, 1).toLowerCase() + filters.field.substring(1, filters.field.length);
    }
    if (filters.filters) {
        camelCaseFilter(filters.filters);
    }
    return filters;
}
const dateFromInterval = (hours) => {
    return new Date(new Date().getTime() + hours * 60000 * 60);
}
const getFieldType = (fieldName) => {
    fieldName = fieldName.toLowerCase();
    for (var i = 0; i < AvailableFields.length; i++) {
        if (AvailableFields[i].FieldName.toLowerCase() === fieldName) {
            return AvailableFields[i].type;
        }
    }
    for (var k = 0; k < AvailableChartFields.length; k++) {
        if (AvailableChartFields[k].FieldName.toLowerCase() === fieldName) {
            return AvailableChartFields[k].type;
        }
    }
}

const getStorageKey = async (filter: unknown, chartType?: string, valueField?: string) => {
    const textAsBuffer = new TextEncoder().encode(JSON.stringify(filter) + (chartType || "") + (valueField || ""));
    const hashBuffer = await window.crypto.subtle.digest("SHA-1", textAsBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hash = hashArray
        .map((item) => item.toString(16).padStart(2, "0"))
        .join("");
    return hash;
}

const DefaultReportName = 'My Dashboard';
const DefaultReportId = 'lb-report-config';

type ErrorBoundaryProps = {
    children?: React.ReactNode;
};

type ErrorBoundaryState = {
    hasError: boolean;
};

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    state: ErrorBoundaryState = { hasError: false };

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        this.setState({ hasError: true });
    }

    render() {
        if (this.state.hasError) {
            return <Typography>Something went wrong.</Typography>;
        }
        return this.props.children;
    }
}

type ChartProps = {
    config: any;
    style?: React.CSSProperties;
    className?: string;
    children?: React.ReactNode;
    companyName?: string;
    saveData?: (key: string, value: any) => void;
    loadData?: (key: string) => any;
    partnerGroups?: any[];
    [key: string]: any;
};

const Chart = (props: ChartProps) => {
    // Explicit `any` — a plain object-literal state literal infers its own
    // (narrower) type; setState later adds properties (e.g. `value`) not present
    // in this initial literal.
    const [state, setStateRaw] = useState<any>({ loading: true, colors: {} });
    const setState = (update: any) => {
        setStateRaw(prev => ({ ...prev, ...(typeof update === 'function' ? update(prev) : update) }));
    };
    // Ad hoc instance field (not React state) holding the in-flight request's
    // cancel token source.
    const axiosCancelSourceRef = useRef<any>(null);
    // componentDidUpdate emulation: a prev-config snapshot plus a didMount guard
    // so the update effect skips the initial commit.
    const didMountRef = useRef(false);
    const prevConfigRef = useRef<any>(props.config);
    // Records which chartType the current state.value was built for, so the render
    // guard never draws the new chart type against the previous type's data shape.
    const dataChartTypeRef = useRef<any>(undefined);

    const getColors = (values) => {
        let colors = {};
        if (values && Array.isArray(values) && values.every(r => !(!r.id))) {
            values.forEach((value, index) => {
                colors[value.id] = defaultColors[index % defaultColors.length];
            });
        }
        return colors;
    };
    const convertToBarValues = (values) => {
        var ret = [];
        var findByDate = (x) => ret.find(r => r.Date === x);
        for (var i = 0; i < values.length; i++) {
            var seriesName = values[i].id;
            for (var z = 0; z < values[i].data.length; z++) {
                var subValue = values[i].data[z];
                var x = moment(subValue.x).format("MMM DD");
                var existing = findByDate(x);
                if (!existing) {
                    existing = { Date: x };
                    ret.push(existing);
                }
                existing[seriesName] = subValue.y;
            }
        }
        return ret;
    };
    const convertToPieValues = (values) => {
        var ret = [];
        for (var i = 0; i < values.length; i++) {
            var seriesName = values[i].id;
            var total = 0;
            for (var z = 0; z < values[i].data.length; z++) {
                var subValue = values[i].data[z];
                total += subValue.y;
            }
            ret.push({ id: seriesName, label: seriesName, value: total });
        }
        return ret;
    };
    const getTickValues = (allTicks, ticks) => {
        var ret = [];
        if (!allTicks || ticks <= 0) {
            return ret;
        }
        var spacing = Math.max(Math.ceil(allTicks.length / ticks), 1);
        for (var i = 0; i < allTicks.length; i += spacing) {
            ret.push(allTicks[i]);
        }
        if (ret.indexOf(allTicks[allTicks.length - 1]) < 0) {
            ret.push(allTicks[allTicks.length - 1]);
        }
        return ret;
    };

    const load = async (disableCache?: boolean) => {
        let entry = { value: null, keys: null, total: null };
        let storageKey = await getStorageKey(props.config.filter, props.config.chartType, props.config.yField);
        if (props.loadData && !disableCache) {
            let storedEntry = props.loadData(storageKey);
            if (storedEntry) {
                entry = storedEntry;
            }
        }
        // Cache is keyed by chartType, so a cached entry matches the current type;
        // an empty entry is null and renders the loader regardless.
        dataChartTypeRef.current = props.config.chartType;
        setState({ value: entry.value, keys: entry.keys, total: entry.total, colors: getColors(entry.value) });
        // moment's type declarations only expose a callable signature (no
        // construct signature); `moment(x)` and `new moment(x)` are
        // equivalent at runtime since moment() itself returns a Moment.
        var now = moment(new Date());
        var startDate = now.add(props.config.interval > 0 ? -1 * props.config.interval : props.config.interval, 'days').toDate();
        var filter = { logic: props.config.filter.logic, operator: props.config.filter.operator, filters: [] };
        for (var k = 0; k < props.config.filter.filters.length; k++) {
            filter.filters.push({ ...props.config.filter.filters[k] });
        }
        filter.filters.push({
            field: "ActivityDateTime",
            operator: "gt",
            value: startDate
        });
        filter.filters.forEach(r => {
            if (r.value === '%CompanyName%') {
                r.value = props.companyName;
            }
        });
        var url = Config.api + '/odata/company/VwActivityHistoryByDay';
        if (props.config.baseUnit === 'hours') {
            url = Config.api + '/odata/company/VwActivityHistoryByHour';
        }
        url += "?";
        url += "$select=ActivityDateTime,CoId,PartnerCoId,CompanyName,PartnerCompanyName";
        if (props.config.yField) {
            url += "," + props.config.yField;
        }
        url += "&$count=true";
        url += "&$filter=" + new FilterOdata({ partnerGroups: props.partnerGroups }).ToOData(filter);
        var res: any = {};
        try {
            res = (await axios.get(url, { cancelToken: axiosCancelSourceRef.current.token })).data;
        } catch (e) {
            if (axios.isCancel(e)) {
                return;
            }
            throw e;
        }
        var values: any = [];
        var keys = [];
        var total = 0;
        if (res.value.length > 0) {
            var yField = props.config.yField || 'NewCount';
            var coid = parseInt(Auth.getAccountNumber());
            var commonCompanyName = res.value.every(item => item.CompanyName === res.value[0].CompanyName && item.CoId === coid);
            var seriesProp = !commonCompanyName ? "CompanyName" : "PartnerCompanyName";
            var sameSenderReceiver = res.value.find(r => r.CompanyName === r.PartnerCompanyName && r.CoId === coid);
            sameSenderReceiver = sameSenderReceiver ? sameSenderReceiver.CompanyName : null;
            if (sameSenderReceiver) {
                res.value = res.value.filter(r => r[seriesProp] !== sameSenderReceiver);
            }
            var series = {};
            total = res.value.map(r => r[yField]).reduce((a, b) => a + b, 0);
            var totals = res.value.reduce((a, item) => {
                a = a || {};
                var existing = a[item[seriesProp]] || { total: 0 };
                if (!a[item[seriesProp]]) {
                    a[item[seriesProp]] = existing;
                }
                existing.total += item[yField];
                return a;
            }, {});
            var xFind = x => r => r.x === x;
            for (var i = 0; i < res.value.length; i++) {
                var item = res.value[i];
                let seriesName = item[seriesProp];
                if (totals[seriesName] && totals[seriesName].total < (total * 0.02)) {
                    seriesName = 'Other';
                }
                if (!series[seriesName]) {
                    series[seriesName] = { id: seriesName, data: [] };
                }
                var x = moment(item.ActivityDateTime).utc().format("YYYY-MM-DDThh:mm");
                if (series[seriesName].data.find(xFind(x))) {
                    series[seriesName].data.find(xFind(x)).y += item[yField];
                } else {
                    series[seriesName].data.push({ x: x, y: item[yField] })
                }
            }
            keys = Object.keys(series);
            values = keys.map(seriesName => series[seriesName]);
            if (props.config.chartType === 'bar') {
                values = convertToBarValues(values);
            }
            else if (props.config.chartType === 'pie') {
                values = convertToPieValues(values);
            }
        }
        dataChartTypeRef.current = props.config.chartType;
        setState({ value: values, keys: keys, total, loading: false, colors: getColors(values) });
        if (props.saveData) {
            props.saveData(storageKey, { value: values, keys, total, lastUpdate: new Date() });
        }
    };

    // componentDidMount + componentWillUnmount
    useEffect(() => {
        axiosCancelSourceRef.current = axios.CancelToken.source();
        load();
        return () => {
            axiosCancelSourceRef.current.cancel();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // componentDidUpdate + shouldComponentUpdate merged: any tracked config field
    // changing triggers a cache-busting reload. The class routed a chartType
    // change through shouldComponentUpdate (returning false) only to also skip the
    // mismatched render while reloading; the render's dataChartTypeRef guard now
    // covers that, so both paths collapse into this single effect.
    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            prevConfigRef.current = props.config;
            return;
        }
        const prev = prevConfigRef.current;
        const cfg = props.config;
        prevConfigRef.current = cfg;
        if (!isEqual(cfg.filter, prev.filter)
            || cfg.baseUnit !== prev.baseUnit
            || cfg.interval !== prev.interval
            || cfg.yField !== prev.yField
            || cfg.chartType !== prev.chartType
        ) {
            load(true);
        }
    });

    var { config, style, className, children, companyName, saveData, loadData, partnerGroups, ...otherProps } = props;
    var wStyle = { color: "black", backgroundColor: "white", display: 'flex' };
    var isDollars = props.config.yField === 'Attribute1' || props.config.yField === 'Attribute2';
    var chart = <ResponsiveLine data={state.value}
        xScale={{ type: 'time', format: '%Y-%m-%dT%H:%M', precision: props.config.baseUnit === 'hours' ? 'hour' : 'day' }}
        yScale={{ type: 'linear', stacked: false }}
        margin={{
            top: 30,
            right: 20,
            bottom: 60,
            left: 40
        }}
        axisBottom={{ format: '%b %d', tickValues: config.w }}
        curve="linear"
        colors={defaultColors}
        dotSize={10}
        enableGridX={false}
        tooltip={(point) => {
            return <div style={{ textAlign: 'left' }}>
                <Typography style={{ fontWeight: '600', fontSize: '16px' }}><Moment format={'MMMM D'}>{point.id}</Moment></Typography>
                {point.data.sort((a, b) => a.serie.id.localeCompare(b.serie.id)).map(r => <div key={r.serie.id} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <ColorBox color={r.serie.color} width={'15px'} height={'15px'} />
                    <Typography>{r.serie.id}:</Typography>
                    <Typography style={{ fontWeight: '600' }}>
                        {isDollars ? dollarFormat.format(r.data.y.toFixed(0)) : Intl.NumberFormat('en-US').format(r.data.y)}
                    </Typography>
                </div>)}</div>;
        }}
        dotBorderWidth={2}
        dotBorderColor="#fff"
        animate={false} />;
    if (config.chartType === 'bar') {
        chart = <AnyResponsiveBar data={state.value}
            margin={{
                top: 30,
                right: 20,
                bottom: 60,
                left: 40
            }}
            axisBottom={{ tickValues: getTickValues(state.value && state.value.map(r => r.Date), 5) }}
            indexBy="Date"
            keys={state.keys}
            colors={defaultColors}
            enableGridX={false}
            tooltip={({ id, color, value, data }) => {
                return <div style={{ textAlign: 'left' }}>
                    <Typography style={{ fontWeight: '600', fontSize: '16px' }}><Moment format={'MMMM D'}>{data.Date}</Moment></Typography>
                    <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                        <ColorBox color={color} width={"15px"} height={"15px"} />
                        <Typography>{id}:</Typography>
                        <Typography style={{ fontWeight: "600" }}>
                            {isDollars ? dollarFormat.format(value.toFixed(0)) : Intl.NumberFormat('en-US').format(value)}
                        </Typography>
                    </div></div>;
            }}
            labelSkipHeight={16}
            animate={false} />;
    }
    if (config.chartType === 'pie') {
        let showLegend = config.w > 3;
        chart = <div style={{ display: 'flex', maxHeight: 'calc(100% - 52px)', maxWidth: '100%', height: 'calc(100% - 52px)' }}>
            <div style={{ flex: '1 1 50%', maxHeight: '100%', maxWidth: showLegend ? '50%' : '100%', display: 'flex', justifyContent: showLegend ? 'right' : 'center' }}>
                <div style={{ aspectRatio: '1 / 1', maxHeight: '100%', marginRight: showLegend ? '10%' : undefined, paddingBottom: '20px' }}>
                    <AnyResponsivePie data={state.value}
                        margin={{
                            top: 0,
                            right: 0,
                            bottom: 0,
                            left: 0
                        }}
                        animate
                        colorBy={r => state.colors[r.id]}
                        enableSlicesLabels={false}
                        tooltip={({ color, id, value }) => {
                            return <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                                <ColorBox color={color} width={"15px"} height={"15px"} />
                                <Typography style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{id}:</Typography>
                                <Typography style={{ fontWeight: "600" }}>
                                    {isDollars ? dollarFormat.format(value.toFixed(0)) : Intl.NumberFormat('en-US').format(value)}
                                </Typography>
                            </div>;
                        }}
                        enableRadialLabels={false}
                        cornerRadius={5}
                        innerRadius={0.7} />
                </div>
            </div>
            {showLegend && <div style={{ flex: 1, maxWidth: '50%', alignContent: 'space-evenly', paddingBottom: '20px' }}>
                <div style={{ display: "flex", justifyContent: "center", flexDirection: "column" }}>
                    {(state.value || []).map(({ id, label, value }) => {
                        return (
                            <div key={id} style={{ display: "flex", gap: "6px", alignItems: "center", maxWidth: '100%', paddingRight: '20px' }}>
                                <div style={{ minWidth: '15px' }}><ColorBox color={state.colors[id]} width={"15px"} height={"15px"} /></div>
                                <Typography style={{ fontWeight: '600', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{label}</Typography>
                                <Typography style={{}}>
                                    {isDollars ? dollarFormat.format(value.toFixed(0)) : Intl.NumberFormat('en-US').format(value)}
                                </Typography>
                            </div>
                        )
                    })}
                </div>
            </div>}
        </div>;
    }
    // Render only the loader until data exists AND matches the current
    // chartType. The chartType half preserves the class's shouldComponentUpdate
    // (which returned false on a chartType change so the new chart type never
    // rendered against the previous type's data shape); dataChartTypeRef records
    // the type the current value was built for.
    if (!state.value || dataChartTypeRef.current !== config.chartType) {
        return <Card className={"widget " + className} {...otherProps} style={Object.assign(wStyle, style)}><ContentLoader basic style={{ flex: 1 }} /></Card>;
    }
    if (state.value.length === 0 || state.value.every(r => r.value === 0)) {
        return <Card className={"widget " + className} {...otherProps} style={Object.assign(wStyle, style)}>
            <CardContent style={{ flex: 1, width: 0, padding: "16px 0 0 0", display: 'flex', flexDirection: 'column' }}>
                <Typography className='chart-title'>{config.title}</Typography>
                <div style={{ flex: 1, alignContent: 'center' }}>
                    There is no data. Try updating the time interval or fields.
                </div>
            </CardContent>
        </Card>;
    }
    return <Card className={"widget " + className} {...otherProps} style={Object.assign(wStyle, style)}>
        <CardContent style={{ flex: 1, width: 0, padding: "16px 0 0 0" }}>
            <Typography className='chart-title'>{config.title}</Typography>
            {config.chartType === 'pie' && <Typography className='chart-title'>Total: {isDollars ? dollarFormat.format(state.total.toFixed(0)) : Intl.NumberFormat('en-US').format(state.total)}</Typography>}
            <ErrorBoundary>
                {state.value ?
                    chart
                    : ''}
            </ErrorBoundary>
            {children}
            {state.loading && <Tooltip title="Data is refreshing" style={{ position: 'absolute', bottom: '2px', right: '2px' }}><RefreshIcon /></Tooltip>}
        </CardContent>
    </Card>;
};

type WidgetProps = {
    config: any;
    style?: React.CSSProperties;
    className?: string;
    children?: React.ReactNode;
    createFilter?: (filter: any) => any;
    saveData?: (key: string, value: any) => void;
    loadData?: (key: string) => any;
    partnerGroups?: any[];
    [key: string]: any;
};

const Widget = (props: WidgetProps) => {
    // Explicit `any` — see Chart's state field above for why (setState later
    // adds value/link, not present in this initial literal).
    const [state, setStateRaw] = useState<any>({ loading: true });
    const setState = (update: any) => {
        setStateRaw(prev => ({ ...prev, ...(typeof update === 'function' ? update(prev) : update) }));
    };
    // Ad hoc instance field (not React state) holding the in-flight request's
    // cancel token source.
    const axiosCancelSourceRef = useRef<any>(null);
    // Prev-prop ref emulating componentDidUpdate's prevProps.config.filter
    // comparison; didMountRef skips the update effect on the initial commit.
    const prevFilterRef = useRef<any>(props.config.filter);
    const didMountRef = useRef(false);
    const capitalize = (str: string) => {
        return str.replace(/(?:^|\s)\S/g, function (a) { return a.toUpperCase(); });
    };
    const load = async () => {
        if (!props.config.filter || !props.config.filter.filters || props.config.filter.filters.length === 0) {
            return;
        }
        let storageKey = await getStorageKey(props.config.filter);
        if (props.loadData) {
            let entry = props.loadData(storageKey);
            if (entry) {
                setState({ value: entry.value });
            }
        }
        let translatedFilter = camelCaseFilter(props.createFilter(props.config.filter));
        //let encodedFilter = window.btoa(unescape(encodeURIComponent(JSON.stringify(translatedFilter)))).replace(/=/g, "_");
        //let link = `/search?filter=${encodedFilter}&title=${capitalize(props.config.title)}`;

        let query = "";
        let prefix = "q";
        let opMapping = {
            "neq": "ne",
            "gte": "ge",
            "lte": "le"
        }
        let baseLink = `/search?title=${capitalize(props.config.title)}`;
        if (translatedFilter && translatedFilter.filters) {
            let filters = translatedFilter.filters;
            let docTypeFilters = filters.filter(r => r.field === "documentType");
            if (docTypeFilters.length > 0 && docTypeFilters.every(r => r.value === "Order" && r.operator === "eq")) {
                filters = filters.filter(r => docTypeFilters.indexOf(r) === -1);
                baseLink = `/order-management?`;
            }
            for (var i = 0; i < filters.length; i++) {
                let op = filters[i].operator;
                op = opMapping[op] || op;
                if (filters[i].value instanceof Date) {
                    if (op === "eq") {
                        filters[i].value = moment(filters[i].value).startOf('day').toISOString();
                        op = "dateeq";
                        filters[i].operator = "dateeq";
                    } else {
                        filters[i].value = moment(filters[i].value).toISOString();
                    }
                }
                query += `&${prefix}.${i}=${filters[i].field} ${op} ${window.encodeURIComponent(filters[i].value)}`;
            }
        }
        let link = baseLink + query;

        let url = Config.api + '/odata/company/Functions.DocumentSearch';
        url += "?";
        url += "$select=id";
        url += "&$top=0";
        url += "&$count=true";
        url += "&$filter=" + new FilterOdata({ partnerGroups: props.partnerGroups }).ToOData(translatedFilter);
        let res = {};
        try {
            res = (await axios.get(url, { cancelToken: axiosCancelSourceRef.current.token })).data;
        } catch (e) {
            if (axios.isCancel(e)) {
                return;
            }
            throw e;
        }
        setState({ value: res["@odata.count"], link: link, loading: false });
        if (props.saveData) {
            props.saveData(storageKey, { value: res["@odata.count"], lastUpdate: new Date() });
        }
    };
    // componentDidMount / componentWillUnmount
    useEffect(() => {
        axiosCancelSourceRef.current = axios.CancelToken.source();
        load();
        return () => {
            axiosCancelSourceRef.current.cancel();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // componentDidUpdate
    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            prevFilterRef.current = props.config.filter;
            return;
        }
        if (!isEqual(props.config.filter, prevFilterRef.current)) {
            load();
        }
        prevFilterRef.current = props.config.filter;
    });

    let { config, style, className, children, createFilter, saveData, loadData, partnerGroups, ...otherProps } = props;
    let bgColor = config.background;
    if ((config.threshold || config.threshold === 0) && config.thresholdBackground && state.value > config.threshold) {
        bgColor = config.thresholdBackground;
    }
    let wStyle = { color: bgColor, backgroundColor: 'white' };
    if ((!state.value && state.value !== 0)) {
        return <Card className={"widget " + className} {...otherProps} style={Object.assign({ display: 'flex' }, style)}><ContentLoader basic style={{ flex: 1 }} />{children}</Card>;
    }
    return <Card className={"widget " + className} {...otherProps} style={Object.assign(wStyle, style)}>
        <CardContent style={{ flex: 1 }}>
            <span className='wtitle'>{config.title}</span>
            <span className='number'>{state.link ? <Link to={state.link}>{Intl.NumberFormat('en-US').format(state.value)}</Link> : state.value} </span>
            {children}
            {state.loading && <Tooltip title="Data is refreshing" style={{ position: 'absolute', bottom: '2px', right: '2px' }}><RefreshIcon /></Tooltip>}
        </CardContent>
    </Card>;
};

type ReportsProps = {
    isRetailer?: boolean;
    companyName?: string;
    cols?: any;
};

type ReportsState = {
    items: DisplayItem[];
    layouts: any;
    showImported: boolean;
    editIndicator: boolean;
    editChart: boolean;
    showColorPicker: boolean;
    showDownload: boolean;
    disableDownload: boolean;
    fileType: string;
    showError: boolean;
    selectedTab: string;
    addWidget: boolean;
    oldData: any;
    pendingChanges: boolean;
    report: any;
    partnerGroups: any[];
    partnerGroupOptions: any[];
    // Many more fields (loaded, cols, indicator, chart, error, downloadItem,
    // availableProfiles, selectedProfile, partnerOptions, statusOptions,
    // docTypeOptions, addWidgetShown, colorType...) are set ad hoc via
    // setState throughout this very dynamic dashboard-builder component.
    [key: string]: any;
};

const reportsFilterFields = [
    { FieldTitle: "Document Type", FieldName: "documentType", type: "strequal" },
    { FieldTitle: "Status", FieldName: "statusDescription", type: "strequal" },
    { FieldTitle: "Status Code", FieldName: "status", type: "number" },
    { FieldTitle: "Sender", FieldName: "companyName", type: "partner" },
    { FieldTitle: "Receiver", FieldName: "partnerCompanyName", type: "partner" },
    { FieldTitle: "Date", FieldName: "date", type: "date" },
    { FieldTitle: "Requested Ship Date", FieldName: "requestedShipDate", type: "date" },
    { FieldTitle: "Status Change Date", FieldName: "statusChangeDate", type: "date" },
    { FieldTitle: "System", FieldName: "source", type: "strequal" }
];

const reportsChartFilterFields = [
    { FieldTitle: "Document Type", FieldName: "DocumentType", type: "strequal" },
    { FieldTitle: "Sender", FieldName: "CompanyName", type: "partner" },
    { FieldTitle: "Receiver", FieldName: "PartnerCompanyName", type: "partner" }
];

const reportsChartYFields = [
    { FieldTitle: "New Count", FieldName: "NewCount" },
    { FieldTitle: "Failure Count", FieldName: "FailureCount" },
    { FieldTitle: "Complete Count", FieldName: "CompleteCount" },
    { FieldTitle: "Duplicate Count", FieldName: "DuplicateCount" },
    { FieldTitle: "Total Amount", FieldName: "Attribute1" },
    { FieldTitle: "Subtotal", FieldName: "Attribute2" },
    { FieldTitle: "Total Quantity", FieldName: "Attribute3" }
];

const reportsChartTypes = [
    { value: 'line', label: 'Line' },
    { value: 'bar', label: 'Bar' },
    { value: 'pie', label: 'Pie' }
];

const Reports = (props: ReportsProps) => {
    const [state, setStateRaw] = useState<ReportsState>({
        items: [], layouts: {}, showImported: false, editIndicator: false, editChart: false, showColorPicker: false,
        showDownload: false, disableDownload: false, fileType: 'csv', showError: false, selectedTab: 'standard', addWidget: false, oldData: { reports: {} },
        pendingChanges: false, report: {}, partnerGroups: [], partnerGroupOptions: []
    });

    // Ad hoc instance fields (not React state) preserved as refs.
    const profilesRef = useRef<any[] | undefined>(undefined);
    const anchorElRef = useRef<any>(null);
    const reportImportRef = useRef<HTMLInputElement | null>(null);

    // Emulates class setState: partial merge (object or updater fn) plus the
    // optional post-commit callback (used by applyWidgetConfig's two-phase
    // items reset). Callbacks fire from an effect after the commit.
    const pendingCallbacksRef = useRef<Array<() => void>>([]);
    const setState = (
        update: Partial<ReportsState> | ((prev: ReportsState) => Partial<ReportsState>),
        cb?: () => void
    ) => {
        if (cb) {
            pendingCallbacksRef.current.push(cb);
        }
        setStateRaw(prev => ({ ...prev, ...(typeof update === 'function' ? (update as (p: ReportsState) => Partial<ReportsState>)(prev) : update) }));
    };
    useEffect(() => {
        if (pendingCallbacksRef.current.length > 0) {
            const cbs = pendingCallbacksRef.current;
            pendingCallbacksRef.current = [];
            cbs.forEach(cb => cb());
        }
    });

    const filterFields = reportsFilterFields;
    const chartFilterFields = reportsChartFilterFields;
    const chartYFields = reportsChartYFields;
    const chartTypes = reportsChartTypes;

    // componentDidMount
    useEffect(() => {
        (async () => {
            clearOldCache();
            let oldData = new StorageHelper().get(getCacheKey());
            if (oldData) {
                setState({ oldData });
            }
            let partnerGroups = await getPartnerGroups();
            let partnerGroupOptions = partnerGroups.map(r => ({ label: r.label, value: r.label }));
            let report = { Name: DefaultReportName, Profile: DefaultDashboard(props.isRetailer), rid: DefaultReportId };
            let items = convertToDisplayItems(report.Profile);
            report.Profile = convertToConfig(items);
            let partners = (await axios.get(Config.api + '/api/v1/Partners')).data.Body.Partners;
            let partnerOptions = partners.map(r => { return { value: r.CompanyName, label: r.CompanyName } });
            partnerOptions.push({ value: props.companyName, label: props.companyName });
            partnerOptions = partnerOptions.sort((a, b) => a.label.localeCompare(b.label))
            let statuses = (await axios.get(Config.api + '/api/v1/Statuses')).data.Body.DocumentTypeStatuses;
            let statusOptions = statuses.map(r => r.StatusDescription);
            statusOptions = statusOptions.filter((r, i) => statusOptions.indexOf(r) === i).map(r => ({ label: r, value: r }));
            let docTypeOptions = statuses.map(r => r.DocumentType);
            docTypeOptions = docTypeOptions.filter((r, i) => docTypeOptions.indexOf(r) === i).map(r => ({ label: r, value: r }));
            setState({ items: items, partnerOptions: partnerOptions, statusOptions, docTypeOptions, report, partnerGroups, partnerGroupOptions, loaded: true });
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function hasPendingChanges(newReport) {
        return !isEqual(state.report.Profile, newReport);
    }
    function getWidgetConfig() {
        if (state.items && state.items.length > 0) {
            return convertToConfig(state.items);
        }
        return state.report?.Profile || {};
    }
    function applyWidgetConfig(config) {
        if (config === null) {
            // Restore default dashboard
            const defaultProfile = DefaultDashboard(props.isRetailer);
            const items = convertToDisplayItems(defaultProfile);
            const report = { Name: DefaultReportName, Profile: convertToConfig(items), rid: DefaultReportId };
            setState({ items: [] }, () => {
                setState({ report, pendingChanges: false, items });
            });
            return;
        }
        // Config may be a widget profile directly ({ Widgets: [...] }) or missing Widgets
        // if it came from an older API format. Ensure it has Widgets before processing.
        const profile = config.Widgets ? config : { Widgets: [] };
        const items = convertToDisplayItems(profile);
        const report = { ...state.report, Profile: convertToConfig(items) };
        setState({ items: [] }, () => {
            setState({ report, pendingChanges: false, items });
        });
    }
    function createFilter(givenFilter: any) {
        givenFilter = JSON.parse(JSON.stringify(givenFilter));
        var createdFilter: any = { filters: [] };
        createdFilter.operator = givenFilter.operator;
        for (var i = 0; i < givenFilter.filters.length; i++) {
            let filter = givenFilter.filters[i];
            if (filter.value === '%CompanyName%') {
                filter.value = props.companyName;
            }
            if (filter.date || (filter.field && filter.field.toLocaleLowerCase().indexOf("date") > -1)) {
                let newFilter: any = {};
                newFilter.field = filter.field;
                newFilter.operator = filter.operator;
                let interval = parseInt(filter.value);
                newFilter.value = dateFromInterval(interval > 0 ? -1 * interval : interval);
                createdFilter.filters.push(newFilter);
            } else if (getFieldType(filter.field) === "number") {
                filter.value = parseInt(filter.value);
                createdFilter.filters.push(filter);
            } else {
                createdFilter.filters.push(filter);
            }
        }
        return createdFilter;
    }
    // Pre-existing (unused in master too); kept as-is to preserve behavior parity.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    function exportConfiguration() {
        var config = JSON.stringify(convertToConfig(state.items));
        var fileName = 'report_configuration.txt';
        var blob = new Blob([config], { type: "application/octet-stream" });
        FileSaver.saveAs(blob, fileName);
    }
    function convertToDisplayItems(reportConfig: DashboardProfile): DisplayItem[] {
        var items = reportConfig.Widgets.map(r => {
            return {
                background: updateColor(r.background),
                thresholdBackground: updateColor(r.thresholdBackground),
                threshold: r.threshold,
                filter: r.filter,
                i: r.id.toString(),
                title: r.title,
                type: r.type,
                x: r.x - 1,
                y: r.y - 1,
                w: r.xSize,
                h: r.ySize,
                baseUnit: r.baseUnit,
                yField: r.yField,
                chartType: r.chartType,
                interval: r.interval
            }
        });
        // this is for backwards compatibility
        items.forEach(item => {
            if (item.type === 'indicator' && item.filter && item.filter.filters) {
                item.filter.filters.forEach(filter => {
                    if (filter.field && filter.field[0].toUpperCase() === filter.field[0]) {
                        filter.field = filter.field[0].toLowerCase() + filter.field.slice(1);
                    }
                })
            }
        })
        return items;
    }
    function convertToConfig(items: DisplayItem[], fromState?: boolean): DashboardProfile {
        let config: DashboardProfile = { Widgets: [] };
        items.forEach(item => {
            let stateItem = fromState ? state.items.find(r => r.i.toString() === item.i) : item;
            config.Widgets.push({
                background: stateItem.background,
                thresholdBackground: stateItem.thresholdBackground,
                threshold: stateItem.threshold,
                filter: stateItem.filter,
                id: parseInt(item.i),
                title: stateItem.title,
                type: stateItem.type,
                x: item.x + 1,
                y: item.y + 1,
                xSize: item.w,
                ySize: item.h,
                baseUnit: stateItem.baseUnit,
                yField: stateItem.yField,
                chartType: stateItem.chartType,
                interval: stateItem.interval
            });
        });
        return config;
    }
    function onLayoutChange(e) {
        var data = convertToConfig(e, true);
        setState({ items: convertToDisplayItems(data), pendingChanges: hasPendingChanges(data) });
    }
    function canDownload(item) {
        if (item.filter && item.filter.filters && item.filter.filters.filter(r => r.field === 'documentType' && r.operator === 'eq').length === 1) {
            return true;
        }
        return false;
    }
    function loadData(key) {
        return (state.oldData.reports || {})[key];
    }
    async function saveData(key, value) {
        setState((prevState) => {
            let reports = { ...prevState.oldData.reports };
            let lastUpdate = prevState.oldData.lastUpdate;
            reports[key] = value;
            let oldData = { lastUpdate, reports };
            if (Object.keys(reports).filter(r => !lastUpdate || moment(reports[r].lastUpdate) > moment(lastUpdate)).length >= prevState.items.length) {
                oldData.lastUpdate = new Date();
                let expiration = new Date();
                expiration.setDate(expiration.getDate() + 7);
                let cutoff = new Date().setDate(new Date().getDate() - 7);
                Object.keys(reports).forEach(r => {
                    if (!reports[r].lastUpdate || moment(reports[r].lastUpdate).valueOf() < cutoff) {
                        delete reports[r];
                    }
                });
                new StorageHelper().set(getCacheKey(), oldData, expiration);
            }
            return { oldData };
        });
    }
    function clearOldCache() {
        let storage = new StorageHelper();
        let cacheEntries = storage.getStartsWith('lb-report-cache-').sort((b, a) => ((a.Value || {}).lastUpdate || '').localeCompare((b.Value || {}).lastUpdate || ''));
        let oldEntries = cacheEntries.slice(10);
        oldEntries.forEach(r => {
            storage.remove(r.Key);
        });
    }
    function getCacheKey() {
        return 'lb-report-cache-' + Auth.getAccountNumber();
    }
    function createElement(item: DisplayItem, index?: number) {
        if (item.type === 'chart') {
            return <div key={item.i} data-grid={item} style={{ display: 'flex' }}>
                <Chart saveData={saveData} loadData={loadData} partnerGroups={state.partnerGroups} style={{ flex: 1 }} config={item} companyName={props.companyName} />
                <Tooltip title="Remove"><CloseIcon className="remove" onClick={() => remove(item)} /></Tooltip>
                <Tooltip title="Edit"><EditIcon className="edit" onClick={() => edit(item)} /></Tooltip>
                <Tooltip title="Copy"><CopyIcon className="copy" onClick={() => copy(item)} /></Tooltip>
            </div>;
        } else {
            return <Widget key={item.i} saveData={saveData} loadData={loadData} partnerGroups={state.partnerGroups} style={{ display: 'flex' }} config={item} data-grid={item} createFilter={createFilter}>
                <Tooltip title="Remove"><CloseIcon className="remove" onClick={() => remove(item)} /></Tooltip>
                <Tooltip title="Edit"><EditIcon className="edit" onClick={() => edit(item)} /></Tooltip>
                <Tooltip title="Copy"><CopyIcon className="copy" onClick={() => copy(item)} /></Tooltip>
                {canDownload(item) && <Tooltip title="Download"><DownloadIcon className="download" onClick={() => showDownload(item)} /></Tooltip>}
            </Widget>;
        }
    }
    async function remove(e) {
        let items = state.items.filter(r => r.i !== e.i);
        setState({ items: items, pendingChanges: hasPendingChanges(convertToConfig(items)) });
    }
    async function copy(e) {
        let currentId = Math.max(0, ...state.items.map(r => parseInt(r.i))) + 1;
        let item = JSON.parse(JSON.stringify(e));
        setLocation(item, state.items);
        item.i = currentId.toString();
        let items = state.items.concat([item]);
        setState({ items: items, pendingChanges: hasPendingChanges(convertToConfig(items)) });
    }
    function edit(e?: any) {
        if (e && e.type === 'indicator' && !state.addWidget) {
            setState({ editIndicator: true, indicator: e, showColorPicker: false });
        }
        else if (e && e.type === 'chart' && !state.addWidget) {
            if (!e.chartType) {
                e.chartType = 'line';
            }
            setState({ editChart: true, chart: e, showColorPicker: false });
        } else {
            let selectedTab = 'standard';
            let chart = { type: 'chart', filter: { filters: [] }, interval: '30', baseUnit: 'days', chartType: 'line', yField: 'NewCount' };
            let indicator = { type: 'indicator', filter: { filters: [] }, background: '#6CBB19' };
            if (e && e.type === 'chart') {
                selectedTab = 'newchart';
                chart = e;
            } else if (e && e.type === 'indicator') {
                selectedTab = 'newtile';
                indicator = e;
            }
            setState({ addWidget: true, addWidgetShown: true, selectedTab, showColorPicker: false, indicator, chart });
        }
    }
    function isCollide(a, b) {
        return !(
            ((a.y + a.h) <= (b.y)) ||
            (a.y >= (b.y + b.h)) ||
            ((a.x + a.w) <= b.x) ||
            (a.x >= (b.x + b.w))
        );
    }
    function setLocation(newItem, existingItems) {
        let cols = state.cols || 12;
        newItem.y = Math.max(0, ...existingItems.map(r => r.y));
        newItem.x = 0;
        for (let y = 0; y < Math.max(0, ...existingItems.map(r => r.y + r.h)); y++) {
            for (let x = 0; x + newItem.w <= cols; x++) {
                let checkObj = { ...newItem, x: x, y: y };
                let count = existingItems.filter(existingItem => isCollide(checkObj, existingItem)).length;
                if (count === 0) {
                    newItem.x = x;
                    newItem.y = y;
                    break;
                }
            }
        }
    }
    async function saveIndicator(e) {
        e = JSON.parse(JSON.stringify(e));
        var items = state.items;
        if (e.i) {
            var filteredItems = items.filter(r => r.i !== e.i);
            items = filteredItems.concat(e);
            setState({ items: items, editIndicator: false, pendingChanges: hasPendingChanges(convertToConfig(items)) });
        } else {
            e.i = (Math.max(...state.items.map(r => parseInt(r.i))) + 1).toString();
            e.w = e.w || 1;
            e.h = e.h || 1;
            setLocation(e, items);
            setState({ items: items.concat(e), editIndicator: false, addWidget: false, pendingChanges: true });
        }
    }
    async function saveChart(e) {
        e = JSON.parse(JSON.stringify(e));
        var items = state.items;
        if (e.i) {
            var filteredItems = items.filter(r => r.i !== e.i);
            items = filteredItems.concat(e);
            setState({ items: items, editChart: false, pendingChanges: hasPendingChanges(convertToConfig(items)) });
        } else {
            e.i = (Math.max(0, ...state.items.map(r => parseInt(r.i))) + 1).toString();
            e.w = 4;
            e.h = 3;
            setLocation(e, items);
            setState({ items: items.concat(e), editChart: false, addWidget: false, pendingChanges: true });
        }
    }
    async function showDownload(e) {
        if (!profilesRef.current) {
            profilesRef.current = await loadProfiles();
        }
        let docType = e.filter.filters.find(r => r.field === 'documentType').value;
        let availableProfiles = profilesRef.current.filter(r => r.DocumentType.toLowerCase() === docType.toLowerCase());
        if (availableProfiles.length === 0) {
            setState({ showError: true, error: 'There are no export profiles available for this report. Please make sure you are filtering to show only one document type.' });
        } else {
            setState({
                showDownload: true,
                disableDownload: false,
                downloadItem: e,
                availableProfiles: availableProfiles,
                selectedProfile: availableProfiles.length > 0 ? availableProfiles[0].Name : null,
            });
        }
    }
    function mapFieldsToApi(filters) {
        if (!filters) {
            return;
        }
        var fieldMap = {
            "status": "StatusCode",
            "statusDescription": "status",
            "companyName": "CompanyName",
            "partnerCompanyName": "PartnerCompanyName",
            "source": "Source",
            "date": "Date",
            "statusChangeDate": "StatusChangeDate",
            "requestedShipDate": "RequestedShipDate"
        };
        var ret = [];
        for (var i = 0; i < filters.length; i++) {
            var fil = { field: filters[i].field, value: filters[i].value, operator: filters[i].operator };
            if (Object.keys(fieldMap).indexOf(fil.field) !== -1) {
                fil.field = fieldMap[fil.field];
                if ((fil.field === "Date" || fil.field === "StatusChangeDate") && fil.value) {
                    let dt = fil.value;
                    fil.value = "DateTimeOffset(DateTime(" + dt.getUTCFullYear() + "," + (dt.getUTCMonth() + 1) + "," + dt.getUTCDate() + "," + dt.getUTCHours() + "," + dt.getUTCMinutes() + "," + dt.getUTCSeconds() + "))";
                }
                else if (fil.field === "RequestedShipDate" && fil.value) {
                    let dt = fil.value;
                    fil.value = "DateTime(" + dt.getFullYear() + "," + (dt.getMonth() + 1) + "," + dt.getDate() + "," + dt.getHours() + "," + dt.getMinutes() + "," + dt.getSeconds() + ")";
                }
                else if (fil.field === "CompanyName" || fil.field === "PartnerCompanyName" || fil.field === "Source" || fil.field === "status") {
                    if (fil.operator !== "ingroup") {
                        fil.value = '"' + fil.value + '"';
                    }
                }
                ret.push(fil);
            }
        }
        return ret;
    }
    async function download() {
        setState({ disableDownload: true });
        var profile = state.availableProfiles.find(r => r.Name === state.selectedProfile);
        var filters = createFilter({ filters: state.downloadItem.filter.filters.filter(r => r.field !== 'documentType') }).filters;
        filters = mapFieldsToApi(filters);
        var fileType = (state.fileType === "legacy" ? "xml" : state.fileType) || 'csv';
        try {
            let link = await getDownloadLink(profile.DocumentType, filters, profile.Fields, fileType);
            return { href: link, fileName: 'export.' + fileType };
        } catch (ex) {
            var error = 'An unexpected error occurred.';
            if (ex.response && ex.response.data) {
                error = new Helpers().getApiErrors(ex.response.data).join("\n")
            }
            setState({ showError: true, error: error });
        }
    }
    async function importConfig(files: FileList | null) {
        if (!files || files.length === 0) {
            return;
        }
        var file = files[0];
        var content = await (new Promise(function (resolve, reject) {
            const reader = new FileReader();
            reader.readAsText(file);
            reader.onload = function () {
                resolve(reader.result);
            };
            reader.onerror = function (error) { reject(error); };
        }));
        // reader.readAsText() always yields a string; FileReader.result's type
        // is a broader union shared with readAsArrayBuffer()/etc.
        var config = JSON.parse(content as string);
        var items = convertToDisplayItems(config);
        var isIndicator = r => r.type === 'indicator';
        var isChart = r => r.type === 'chart';
        var indicatorSelector = r => ({ filter: r.filter, title: r.title });
        var chartSelector = r => ({ filter: r.filter, title: r.title, interval: r.interval, yField: r.yField, baseUnit: r.baseUnit });
        var indicatorsToCompare = state.items.filter(isIndicator).map(indicatorSelector);
        var chartsToCompare = state.items.filter(isChart).map(chartSelector);
        var newIndicators = items.filter(isIndicator).filter(r => !indicatorsToCompare.find(x => isEqual(x, indicatorSelector(r))));
        var newCharts = items.filter(isChart).filter(r => !chartsToCompare.find(x => isEqual(x, chartSelector(r))));
        var currentId = Math.max(0, ...state.items.map(r => parseInt(r.i))) + 1;
        var maxY = items.length === 0 ? 0 : Math.max(...items.map(r => r.y)) + 1;
        var message = `Imported ${newIndicators.length + newCharts.length} of ${items.length} report(s).`;
        if (items.length > (newIndicators.length + newCharts.length)) {
            message += ' Duplicate reports were not imported.';
        }
        if (newIndicators.length > 0 || newCharts.length > 0) {
            var currentItems = state.items.slice();
            for (var i = 0; i < newIndicators.length; i++) {
                newIndicators[i].i = currentId.toString();
                newIndicators[i].y = maxY > 0 ? maxY + (newIndicators[i].y || 0) : newIndicators[i].y;
                currentItems.push(newIndicators[i]);
                currentId++;
            }
            for (var k = 0; k < newCharts.length; k++) {
                newCharts[k].i = currentId.toString();
                newCharts[k].y = maxY > 0 ? maxY + (newCharts[k].y || 0) : newCharts[k].y;
                currentItems.push(newCharts[k]);
                currentId++;
            }
            setState({ items: currentItems });
        }
        setState({ showImported: true, importMessage: message });
        reportImportRef.current.value = null;
    }
    function getFilterValueEditor(filter, name, operator) {
        let fieldName = filter && filter.field ? filter.field.toLowerCase() : null;
        if (fieldName === "companyname" || fieldName === "partnercompanyname") {
            if (operator === "ingroup") {
                return <Field component={AutoComplete} label='Value' name={`${name}.value`} options={state.partnerGroupOptions} validate={v => !v ? "Filter value is required." : ""} />;
            }
            return <Field component={AutoComplete} label='Value' name={`${name}.value`} options={state.partnerOptions} validate={v => !v ? "Filter value is required." : ""} />;
        } else if (fieldName === "date" || fieldName === "statuschangedate" || fieldName === "requestedshipdate") {
            return <Field component={ExtendedField} label='Value' name={`${name}.value`} endAdornment={<InputAdornment position="end">hours ago</InputAdornment>} validate={v => !v ? "Filter value is required." : ""} />;
        } else if (fieldName === "documenttype") {
            return <Field component={AutoComplete} label='Value' name={`${name}.value`} options={state.docTypeOptions} validate={v => !v ? "Filter value is required." : ""} />;
        } else if (fieldName === "statusdescription") {
            return <Field component={AutoComplete} label='Value' name={`${name}.value`} options={state.statusOptions} validate={v => !v ? "Filter value is required." : ""} />;
        }
        return <Field component={ExtendedField} label='Value' name={`${name}.value`} validate={v => !v ? "Filter value is required." : ""} />;
    }
    function renderIndicatorForm(forceEnableSave?: boolean) {
        let backgrounds = state.items.map(r => r.background).concat(state.items.map(r => r.thresholdBackground)).filter(r => !(!r)).concat(defaultColors).map(r => (r || "").toLowerCase());
        return state.indicator ?
            <Form onSubmit={saveIndicator}
                initialValues={state.indicator}
                mutators={{
                    ...arrayMutators
                }}
                render={({ handleSubmit, pristine, invalid, values }) => (
                    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column' }}>
                        <DialogContent>
                            <div style={{ display: 'flex', flexDirection: 'column', flexShrink: '0', gap: '1rem' }}>
                                <Field label='Title *' component={ExtendedField} name='title' validate={v => !(!v) ? '' : 'Title is required.'} />
                                <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
                                    <Field component={ColorButton} label='Color *' buttonRef={(node: any) => { anchorElRef.current = node; }} onClick={() => setState({ showColorPicker: true, colorType: 'background' })} name='background' />
                                    <Field label='Threshold (number of documents)' component={ExtendedField} type='number' name='threshold' style={{ width: '18rem', paddingTop: '16px' }} />
                                    <Field component={ColorButton} style={{ flex: '0 0 auto' }} label='Color after threshold' buttonRef={(node: any) => { anchorElRef.current = node; }} onClick={() => setState({ showColorPicker: true, colorType: 'thresholdBackground' })} name='thresholdBackground' />
                                </div>
                                <Popper style={{ zIndex: 1400 }} open={state.showColorPicker} anchorEl={anchorElRef.current} transition>
                                    {({ TransitionProps, placement }) => (
                                        <Grow
                                            {...TransitionProps}
                                            style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
                                        >
                                            <Paper>
                                                <ClickAwayListener onClickAway={() => setState({ showColorPicker: false })}>
                                                    <div>
                                                        <Field component={ColorPicker} presetColors={backgrounds.filter((r, i) => r && backgrounds.indexOf(r) === i)} name={state.colorType} />
                                                    </div>
                                                </ClickAwayListener>
                                            </Paper>
                                        </Grow>
                                    )}
                                </Popper>
                                <div style={{ marginTop: '1rem' }}><Typography>Select documents where</Typography></div>
                                <FieldArray name="filter.filters">
                                    {({ fields }) => (
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            {fields.map((name, index) => (
                                                <div key={name}><div style={{ display: 'flex', flexDirection: 'row', padding: '0.25em', alignItems: 'flex-start' }}>
                                                    <div style={{ width: '9em', minWidth: '9em', flexDirection: 'column', display: 'flex', justifyContent: 'flex-end' }}>
                                                        <Field component={Select} style={{ textAlign: 'left' }} placeholder='Select field...' label='Field' name={`${name}.field`}>
                                                            {filterFields.map(r => <MenuItem key={r.FieldName} value={r.FieldName}>{r.FieldTitle}</MenuItem>)}
                                                        </Field>
                                                    </div>
                                                    <div style={{ width: '8em', minWidth: '8em', flexDirection: 'column', display: 'flex', justifyContent: 'flex-end', marginLeft: '0.5em', marginRight: '0.5em' }}>
                                                        {values.filter.filters[index] && values.filter.filters[index].field ?
                                                            <Field style={{ flex: '1 1 auto', textAlign: 'left' }} component={Select} label='Operator' name={`${name}.operator`}>
                                                                {getOperators(values.filter.filters[index].field, filterFields, state.partnerGroups.length > 0).map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                                                            </Field>
                                                            : ''}
                                                    </div>
                                                    <div style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column' }}>
                                                        {values.filter.filters[index] && values.filter.filters[index].field && values.filter.filters[index].operator ?
                                                            getFilterValueEditor(values.filter.filters[index], name, values.filter.filters[index].operator)
                                                            : ''}
                                                    </div>
                                                    <div style={{ alignSelf: 'center' }}><IconButton size='small' onClick={() => fields.remove(index)}><CloseIcon /></IconButton></div>
                                                </div>
                                                    <Divider /></div>
                                            ))}
                                            <Button variant='outlined' color='primary' onClick={() => fields.push({ field: 'documentType', operator: 'eq', value: '' })} style={{ marginTop: '0.5em'}}>+ Add Filter</Button>
                                        </div>
                                    )}
                                </FieldArray>
                            </div>
                        </DialogContent>
                        <DialogActions>
                            <Button variant='text' onClick={() => setState({ addWidget: false, editChart: false, editIndicator: false })}>Cancel</Button>
                            <Button variant='text' type='submit' disabled={(pristine && !forceEnableSave) || invalid} color="primary">
                                {state.addWidget ? 'Add' : 'Save'}
                            </Button>
                        </DialogActions>
                    </form>
                )} />
            : '';
    }
    function renderChartForm(forceEnableSave?: boolean) {
        return state.chart ?
            <Form onSubmit={saveChart}
                initialValues={state.chart}
                mutators={{
                    ...arrayMutators
                }}
                render={({ handleSubmit, pristine, invalid, values }) => (
                    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column' }}>
                        <DialogContent>
                            <div style={{ display: 'flex', flexDirection: 'column', flexShrink: '0', overflow: 'hidden', gap: '1rem' }}>
                                <Field label='Title *' component={ExtendedField} name='title' validate={v => !(!v) ? '' : 'Title is required.'} />
                                <div style={{ display: 'flex' }}>
                                    <Field component={Select} style={{ textAlign: 'left' }} formControlProps={{ style: { flex: 1, marginRight: '0.5rem' } }} placeholder='Select chart type...' label='Chart Type *' name='chartType'>
                                        {chartTypes.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                                    </Field>
                                    <Field component={Select} style={{ textAlign: 'left' }} formControlProps={{ style: { flex: 1, marginRight: '0.5rem' } }} placeholder='Select field...' label='Value Field *' name='yField'>
                                        {chartYFields.map(r => <MenuItem key={r.FieldName} value={r.FieldName}>{r.FieldTitle}</MenuItem>)}
                                    </Field>
                                </div>
                                <div style={{ display: 'flex', flex: 1, alignItems: 'center', position: 'relative', height: '3em', paddingTop: '0.5em' }}>
                                    <InputLabel shrink style={{ position: 'absolute', top: 0 }}>Interval (days)</InputLabel>
                                    <Field label='Interval (days)' style={{ flex: 1, marginRight: '1rem', paddingTop: '5px', paddingBottom: '5px' }} component={ExtendedSlider} format={Math.abs} max={180} min={1} step={1} name='interval' />
                                    <Field component={ExtendedField} type='number' min="1" max="180" step="1" name='interval' style={{ width: '3em' }} />
                                </div>
                                <div style={{ marginTop: '1rem' }}><Typography>Select documents where</Typography></div>
                                <FieldArray name="filter.filters">
                                    {({ fields }) => (
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            {fields.map((name, index) => (
                                                <div key={name}><div style={{ display: 'flex', flexDirection: 'row', padding: '0.25em', alignItems: 'flex-start' }}>
                                                    <div style={{ flex: 1, flexDirection: 'column', display: 'flex', justifyContent: 'flex-end' }}>
                                                        <Field component={Select} style={{ textAlign: 'left' }} placeholder='Select field...' label='Field' name={`${name}.field`}>
                                                            {chartFilterFields.map(r => <MenuItem key={r.FieldName} value={r.FieldName}>{r.FieldTitle}</MenuItem>)}
                                                        </Field>
                                                    </div>
                                                    <div style={{ flex: 1, flexDirection: 'column', display: 'flex', justifyContent: 'flex-end', marginLeft: '0.5em', marginRight: '0.5em' }}>
                                                        {values.filter.filters[index] && values.filter.filters[index].field ?
                                                            <Field style={{ flex: 1, textAlign: 'left' }} component={Select} label='Operator' name={`${name}.operator`}>
                                                                {getOperators(values.filter.filters[index].field, chartFilterFields, state.partnerGroups.length > 0).map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                                                            </Field>
                                                            : ''}
                                                    </div>
                                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                                        {values.filter.filters[index] && values.filter.filters[index].field && values.filter.filters[index].operator ?
                                                            getFilterValueEditor(values.filter.filters[index], name, values.filter.filters[index].operator)
                                                            : ''}
                                                    </div>
                                                    <div style={{ alignSelf: 'center' }}><IconButton size='small' onClick={() => fields.remove(index)}><CloseIcon /></IconButton></div>
                                                </div>
                                                    <Divider /></div>
                                            ))}
                                            <Button variant='outlined' color='primary' onClick={() => fields.push({ field: 'DocumentType', operator: 'eq', value: '' })} style={{ marginTop: '0.5em'}}>+ Add Filter</Button>
                                        </div>
                                    )}
                                </FieldArray>
                            </div>
                        </DialogContent>
                        <DialogActions>
                            <Button variant='text' onClick={() => setState({ addWidget: false, editChart: false, editIndicator: false })}>Cancel</Button>
                            <Button variant='text' type='submit' disabled={(pristine && !forceEnableSave) || invalid} color="primary">
                                {state.addWidget ? 'Add' : 'Save'}
                            </Button>
                        </DialogActions>
                    </form>
                )} />
            : '';
    }
    function renderStandardForm() {
        let { isRetailer } = props;
        let widgets = DefaultDashboard(isRetailer).Widgets.concat(isRetailer ? StandardRetailer.Widgets : StandardSupplier.Widgets);
        // `any`: this starts as DisplayItem[] but is immediately reshaped below
        // into { item, exists } gallery entries for the "Add Widget > Standard" tab.
        let displayItems: any = convertToDisplayItems({ Widgets: widgets });
        displayItems = displayItems.map(r => ({ item: { ...r, id: null, i: null }, exists: state.items.filter(x => x.title === r.title && x.type === r.type).length > 0 }));
        displayItems = displayItems.sort((a, b) => {
            if (a.exists !== b.exists) {
                return a.exists ? 1 : -1;
            }
            return b.item.type.localeCompare(a.item.type);
        });
        return (
            <CardContent style={{ overflow: 'auto' }}>
                <Grid container spacing={2} style={{ display: 'flex', textAlign: 'center', fontSize: '14px' }}>
                    {displayItems.map((r, i) => <Grid key={i} size={{ xs: 6, sm: 6, md: 6, lg: 6 }}>
                        {r.item.type === "chart" && <Chart config={r.item} style={{ height: '15rem', filter: r.exists ? 'grayscale(1)' : null }} companyName={props.companyName} />}
                        {r.item.type === "indicator" && <Widget style={{ height: '110px', filter: r.exists ? 'grayscale(1)' : null }} config={r.item} createFilter={createFilter} />}
                        <div style={{ textAlign: 'right' }}>
                            {r.exists && `This ${r.item.type === 'indicator' ? 'tile' : r.item.type} is already on your dashboard.`}
                            <Tooltip title="Edit"><IconButton onClick={() => edit({ ...r.item })} size="large"><EditIcon className="edit" /></IconButton></Tooltip>
                            <Tooltip title="Add"><IconButton
                                color='primary'
                                onClick={() => { copy({ ...r.item }); setState({ addWidget: false }); }}
                                size="large"><AddCircleIcon className="add" /></IconButton></Tooltip>
                        </div>
                    </Grid>)}
                </Grid>
            </CardContent>
        );
    }
    return (
        <>
            <Grid container spacing={2} style={{ width: '100%', flexDirection: 'column' }}>
                {state.loaded && <><Grid size={{ xs: 12, sm: 12, md: 12 }} style={{ justifyContent: 'right', paddingTop: '20px', display: 'flex', gap: '0.5em' }}>
                    <SavedSearchManager
                        tableType="dashboard"
                        profileType="StandardReport"
                        storageKeyPrefix="lb-report-config"
                        configMode="opaque"
                        tableState={getWidgetConfig()}
                        onConfigApplied={(config) => applyWidgetConfig(config)}
                        defaultProfiles={[{
                            id: DefaultReportId,
                            name: DefaultReportName,
                            tableType: 'dashboard',
                            configuration: DefaultDashboard(props.isRetailer),
                            visibility: 'private',
                            isDefault: false,
                            isSystemProfile: false
                        }, {
                            id: 'lb_standard',
                            name: 'Logicbroker Standard Dashboard',
                            tableType: 'dashboard',
                            configuration: DefaultDashboard(props.isRetailer),
                            visibility: 'private',
                            isDefault: false,
                            isSystemProfile: true,
                            partnerCompanyName: 'Logicbroker'
                        }]}
                    />
                    <Button variant='contained' color='primary' onClick={() => edit()} startIcon={<AddIcon />} style={{ whiteSpace: 'nowrap' }}>Add Widget</Button>
                </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 12 }} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        {state.oldData.lastUpdate && <Typography>Last Update: <Moment format='MM/DD/YYYY hh:mm A'>{state.oldData.lastUpdate}</Moment></Typography>}
                    </Grid></>}
                <Grid size={{ xs: 12, sm: 12, md: 12 }} style={{ padding: "0px" }}>
                    <ResponsiveReactGridLayout
                        layouts={{ lg: state.items, md: state.items, sm: state.items, xs: state.items, xxs: state.items }}
                        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                        cols={props.cols || { lg: 10, md: 8, sm: 6, xs: 4, xxs: 2 }}
                        rowHeight={125}
                        measureBeforeMount={false}
                        onDragStop={onLayoutChange}
                        onResizeStop={onLayoutChange}
                        onBreakpointChange={(newBreakpoint, newCols) => setState({ cols: newCols })}
                        compactType={'vertical'}
                        preventCollision={false}
                    >
                        {state.items.map((el, i) => createElement(el, i))}
                    </ResponsiveReactGridLayout>
                </Grid>
            </Grid>
            <input type="file" accept='.txt,.json' id="reportImport" ref={reportImportRef} style={{ display: "none" }} onChange={(e) => importConfig(e.target.files)} />
            <Dialog
                maxWidth="sm"
                open={state.showImported}
                onClose={() => setState({ showImported: false })}
            >
                <Toolbar className='lbtoolbar'>Configuration Imported</Toolbar>
                <DialogContent>
                    <Typography>{state.importMessage}</Typography>
                </DialogContent>
            </Dialog>
            <Dialog open={state.showError} onClose={() => { setState({ showError: false }) }}>
                <Toolbar className='lbtoolbar'>Error</Toolbar>
                <DialogContent><Typography>{state.error}</Typography></DialogContent>
            </Dialog>
            <Dialog
                maxWidth="sm"
                open={state.showDownload}
                onClose={(event, reason) => {
                    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                        if (state.disableDownload) return;
                    }
                    setState({ showDownload: false });
                }}>
                <Toolbar className='lbtoolbar'>Download Documents</Toolbar>
                <DialogContent>
                    Export profile
                    {state.availableProfiles && <RadioGroup style={{ flexDirection: 'column' }} value={state.selectedProfile} onChange={(e) => setState({ selectedProfile: e.target.value })}>
                        {state.availableProfiles.map(r => <FormControlLabel key={r.Name} value={r.Name} control={<Radio />} label={r.Name} />)}
                    </RadioGroup>}
                    File type
                    <RadioGroup style={{ flexDirection: 'column' }} value={state.fileType} onChange={(e) => setState({ fileType: e.target.value })}>
                        <FormControlLabel value='csv' control={<Radio />} label='CSV' />
                        <FormControlLabel value='xlsx' control={<Radio />} label='XLSX' />
                    </RadioGroup>
                </DialogContent>
                <DialogActions>
                    <DownloadButton variant='text' onError={(e) => setState({ showError: true, error: e, disableDownload: false })} color='primary' handleComplete={() => { setState({ showDownload: false }) }} disabled={state.disableDownload || !state.fileType || !state.selectedProfile} onClick={download}>Download</DownloadButton>
                </DialogActions>
            </Dialog>
            <Dialog
                maxWidth="md"
                fullWidth
                open={state.editIndicator}
                onClose={() => setState({ editIndicator: false })}
            >
                <Toolbar className='lbtoolbar'>Edit Tile</Toolbar>
                {renderIndicatorForm()}
            </Dialog>
            <Dialog
                maxWidth="md"
                fullWidth
                open={state.editChart}
                onClose={() => setState({ editChart: false })}
            >
                <Toolbar className='lbtoolbar'>Edit Chart</Toolbar>
                {renderChartForm()}
            </Dialog>
            <Dialog
                maxWidth='md'
                fullWidth
                open={state.addWidget}
                onClose={() => setState({ addWidget: false })}
            >
                <Toolbar className='lbtoolbar'>Add a Widget</Toolbar>
                <Tabs value={state.selectedTab} onChange={(e, newValue) => setState({ selectedTab: newValue })} style={{ paddingLeft: '24px', paddingRight: '24px' }}>
                    <Tab label='Standard' value='standard'></Tab>
                    <Tab label='New Tile' value='newtile'></Tab>
                    <Tab label='New Chart' value='newchart'></Tab>
                </Tabs>
                {state.selectedTab === 'standard' && state.addWidgetShown && renderStandardForm()}
                {state.selectedTab === 'newtile' && renderIndicatorForm(true)}
                {state.selectedTab === 'newchart' && renderChartForm(true)}
            </Dialog>
        </>
    );
}

// Both ColorPicker and ColorButton are used only as react-final-form
// `component={...}` render targets (via the `Field` cast to any above), which
// injects the `input`/`meta` render props alongside any custom props
// (presetColors, buttonRef, onClick, label, style, name...).
type ColorPickerProps = {
    input: {
        name: string;
        onChange: (value: any) => void;
        value: any;
    };
    meta?: any;
    alwaysShowError?: boolean;
    [key: string]: any;
};

const ColorPicker = (props: ColorPickerProps) => {
    var {
        input: { name, onChange, value },
        meta,
        alwaysShowError,
        ...rest
    } = props;
    return <Picker
        {...rest}
        name={name}
        onChange={color => { onChange(color.hex) }}
        color={value}
    />;
};

type ColorButtonProps = {
    input: {
        value: any;
    };
    meta?: any;
    alwaysShowError?: boolean;
    label?: React.ReactNode;
    style?: React.CSSProperties;
    [key: string]: any;
};

const ColorButton = (props: ColorButtonProps) => {
    var {
        input: { value },
        meta,
        alwaysShowError,
        label,
        style,
        ...rest
    } = props;
    return <div style={style}><InputLabel shrink style={{ whiteSpace: 'nowrap' }}>{label}</InputLabel><Button {...rest} style={{ backgroundColor: value, height: '30px', width: '80px', cursor: 'pointer', border: '1px solid lightgray' }}> </Button></div>;
};

export { getFontColor };

export default Reports;